import { useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { api } from "@utils/api";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";
import { useAuth } from "@contexts/auth";
import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue,useDisclosure, useMounted } from "@mantine/hooks";
import { IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

// Standard table components following AdminDashboard/StaffDashboard patterns
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";
import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { DEFAULT_PAGINATION, QP } from "@constants/index";

import { useUpdateProfile } from "@hooks/mutations/profile/useUpdateProfile";
import { useGetProfiles } from "@hooks/queries/profile/useGetProfiles";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { Profile } from "@customTypes/profile";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

import { AddDoctorModal } from "./components/AddDoctorModal";
import { AddLaboratoryModal } from "./components/AddLaboratoryModal";
import { EditDoctorModal } from "./components/EditDoctorModal";
import { EditLaboratoryModal } from "./components/EditLaboratoryModal";


export const ProfilesTab = () => {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const client = useQueryClient();
  const handleErrors = useQueryErrorHandling();
  const archive = useMutation({
    mutationFn: (id: string) => api.delete(`profile/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['profiles'] }),
    onError: error => handleErrors(error, 'mutation'),
  });
  const confirmArchive = (id: string) => modals.openConfirmModal({
    title: t('archive-profile'), children: <Text>{t('archive-profile-confirm')}</Text>,
    labels: { confirm: t('access.actions.archive'), cancel: t('cancel') },
    onConfirm: () => archive.mutate(id),
  });

  // Tab state
  const [activeTab, setActiveTab] = useState("doctors");

  // Table column count for skeleton loading states
  const { tableRef, columnCount } = useTableColumnCount();

  // Search and pagination for doctors using established patterns
  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });

  // Search and pagination state for laboratories (keeping existing pattern)
  const [laboratoriesSearch, setLaboratoriesSearch] = useState("");
  const [laboratoriesDebounced] = useDebouncedValue(laboratoriesSearch, 400);

  // Table data state
  const [tableData, setTableData] = useState<Profile[]>([]);
  const [laboratoriesData, setLaboratoriesData] = useState<Profile[]>([]);
  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();

  // Laboratories pagination state
  const [laboratoriesPagination, setLaboratoriesPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    pageCount: 1,
    total: 0,
  });

  // API call for doctors data using profiles endpoint
  const {
    data,
    isLoading,
    isRefetching,
    fetchStatus,
    isSuccess,
    isError,
  } = useGetProfiles({
    pagination: tablePagination,
    search: debounced,
    type: "doctor",
    visibility: "all",
    isEnabled: activeTab === "doctors",
  });

  // API call for laboratories data using profiles endpoint
  const {
    data: laboratoriesApiData,
    isLoading: isLaboratoriesLoading,
    fetchStatus: laboratoriesFetchStatus,
    isSuccess: isLaboratoriesSuccess,
    isError: isLaboratoriesError,
  } = useGetProfiles({
    pagination: laboratoriesPagination,
    search: laboratoriesDebounced,
    type: "lab",
    visibility: "all",
    isEnabled: activeTab === "laboratories",
  });

  // Modal states
  const [addDoctorModalOpened, { open: openAddDoctorModal, close: closeAddDoctorModal }] = useDisclosure(false);
  const [addLaboratoryModalOpened, { open: openAddLaboratoryModal, close: closeAddLaboratoryModal }] = useDisclosure(false);
  const [editDoctorModalOpened, { open: openEditDoctorModal, close: closeEditDoctorModal }] = useDisclosure(false);
  const [editLaboratoryModalOpened, { open: openEditLaboratoryModal, close: closeEditLaboratoryModal }] = useDisclosure(false);

  // State for tracking which profile is being updated
  const [currentUpdatingProfileId, setCurrentUpdatingProfileId] = useState<string | null>(null);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);

  // Profile update mutation
  const { mutate: updateProfile, isLoading: isUpdatingProfile } = useUpdateProfile();

  // Doctor table rows (AdminDashboard pattern)
  const doctorRows = useMemo(() => {
    return tableData?.map(profile => (
      <Table.Tr key={profile.id}>
        <Table.Td>
          <Group gap={12} align="center">
            <Avatar size={32} radius="xl" src={profile.profileImage} />
            <Text fz={14} fw={500} lh={1.5}>
              {profile.userInfo?.fullName || "-"}
            </Text>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {profile.licenseNumber || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {profile.specialization || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          {(() => {
            const specialties = profile.specialties;
            if (!specialties || specialties.length === 0) {
              return (
                <Text fz={14} fw={400} lh={1.5} c="dimmed">
                  -
                </Text>
              );
            }

            if (specialties.length === 1) {
              return (
                <Text fz={14} fw={400} lh={1.5} c="dimmed">
                  {specialties[0]}
                </Text>
              );
            }

            const remainingCount = specialties.length - 1;
            return (
              <Group gap={8} align="center">
                <Text fz={14} fw={400} lh={1.5} c="dimmed">
                  {specialties[0]}
                </Text>
                <Badge
                  size="xs"
                  variant="light"
                  color="gray"
                  fz={11}
                >
                  {remainingCount} more
                </Badge>
              </Group>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Button
            w={"auto"}
            tt={"capitalize"}
            variant="light"
            size="compact-sm"
            color={profile.visibility === "public" ? "blue" : "gray"}
            loading={
              isUpdatingProfile && currentUpdatingProfileId === profile.id
            }
            disabled={!can("profiles.publish") || !can("profiles.edit")} onClick={(e) => {
              console.log("Visibility button clicked for profile:", profile.id);
              setCurrentUpdatingProfileId(profile.id);
              const newVisibility = profile.visibility === "public" ? "private" : "public";

              const formData = new FormData();
              formData.append("visibility", newVisibility);

              updateProfile(
                {
                  profileId: profile.id,
                  profileData: formData,
                },
                {
                  onSuccess: () => {
                    console.log("Visibility update success for profile:", profile.id);
                    setCurrentUpdatingProfileId(null);
                  },
                  onError: () => {
                    console.log("Visibility update error for profile:", profile.id);
                    setCurrentUpdatingProfileId(null);
                  },
                }
              );
              e.stopPropagation();
            }}
          >
            {profile.visibility === "public" ? "Public" : "Private"}
          </Button>
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="profiles.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedProfileUserId(profile.userId);
                  openEditDoctorModal();
                }}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="profiles.archive"><ActionIcon
                variant="outline"
                color="gray"
                size="sm"
                disabled={archive.isLoading} onClick={() => confirmArchive(profile.id)}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [tableData, i18n.language, isUpdatingProfile, currentUpdatingProfileId, can, archive.isLoading]);

  // Laboratory table rows (AdminDashboard pattern)
  const laboratoryRows = useMemo(() => {
    return laboratoriesData?.map(profile => (
      <Table.Tr key={profile.id}>
        <Table.Td>
          <Group gap={8} align="center">
            <Avatar
              size={32}
              radius="xl"
              src={profile.profileImage}
              color="blue"
              variant="light"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
            <Text fz={14} fw={500} lh={1.5}>
              {profile.partnerInfo?.name || "-"}
            </Text>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {profile.location || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {profile.yearsOfExperience || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          {(() => {
            const value = profile.briefDescription;
            const isLong = value && value.length > 30;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={400}
                  lh={1.5}
                  c="dimmed"
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value || "-"}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          {(() => {
            const visibility = profile.visibility;
            // Handle cases where visibility field might not be present in laboratory profiles
            const isPublic = visibility === "public" || visibility === "Public";
            const displayText = visibility ? (isPublic ? "Public" : "Private") : "Public"; // Default to Public if not specified

            return (
              <Button
                w={"auto"}
                tt={"capitalize"}
                variant="light"
                size="compact-sm"
                color={isPublic ? "blue" : "gray"}
                loading={
                  isUpdatingProfile && currentUpdatingProfileId === profile.id
                }
                disabled={!can("profiles.publish") || !can("profiles.edit")} onClick={(e) => {
                  console.log("Lab visibility button clicked for profile:", profile.id);
                  setCurrentUpdatingProfileId(profile.id);
                  const newVisibility = isPublic ? "private" : "public";

                  const formData = new FormData();
                  formData.append("visibility", newVisibility);

                  updateProfile(
                    {
                      profileId: profile.id,
                      profileData: formData,
                    },
                    {
                      onSuccess: () => {
                        console.log("Lab visibility update success for profile:", profile.id);
                        setCurrentUpdatingProfileId(null);
                      },
                      onError: () => {
                        console.log("Lab visibility update error for profile:", profile.id);
                        setCurrentUpdatingProfileId(null);
                      },
                    }
                  );
                  e.stopPropagation();
                }}
              >
                {displayText}
              </Button>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="profiles.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedProfileUserId(profile.userId);
                  openEditLaboratoryModal();
                }}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="profiles.archive"><ActionIcon
                variant="outline"
                color="gray"
                size="sm"
                disabled={archive.isLoading} onClick={() => confirmArchive(profile.id)}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [laboratoriesData, i18n.language, isUpdatingProfile, currentUpdatingProfileId, can, archive.isLoading]);

  // TODO: Re-enable mock table objects when implementing pagination
  // const mockDoctorsTable = {
  //   getPaginationRowModel: () => ({
  //     rows: tableData,
  //   }),
  // };

  // const mockLaboratoriesTable = {
  //   getPaginationRowModel: () => ({
  //     rows: laboratoriesData,
  //   }),
  // };

  // Effects for data management following established patterns
  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data, setTablePagination]);

  useEffect(() => {
    if (laboratoriesApiData) {
      setLaboratoriesData(laboratoriesApiData.newData);
      setLaboratoriesPagination(laboratoriesApiData.pagination);
    } else {
      setLaboratoriesData([]);
    }
  }, [laboratoriesApiData]);

  useEffect(() => {
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <StyledLaboratory>
      <StyledResourceManagement>
        {/* <LoadingOverlay
          visible={isRefetching && !!data?.newData?.length && !isMounted && !isUpdatingProfile}
          zIndex={1000}
          overlayProps={{ blur: 0.1, opacity: 0.6 }}
          loaderProps={{ display: "none" }}
        /> */}

        <TabsRoot
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as string)}
        >
          <div className="header">
            <Group w={"100%"} justify="space-between" align="center">
              <h1>{t("profiles")}</h1>
              <TabsList
                comboboxItems={[
                  { label: t("doctors"), value: "doctors" },
                  { label: t("laboratory-actual"), value: "laboratories" },
                ]}
              />
            </Group>
          </div>

          <Group w={"100%"} justify="space-between" align="center">
            <TextInput
              mt={"md"}
              placeholder={activeTab === "doctors"
                ? t("search-for-a-doctor")
                : t("search-for-a-laboratory")}
              leftSection={<IconSearch size={16} />}
              value={activeTab === "doctors" ? searchFilter : laboratoriesSearch}
              onChange={event => {
                if (activeTab === "doctors") {
                  setSearchFilter(event.currentTarget.value);
                } else {
                  setLaboratoriesSearch(event.currentTarget.value);
                }
              }}
              style={{ flex: 1, maxWidth: 400 }}
            />
            <PermissionGate permission="profiles.create"><Button
              leftSection={<IconPlus size={16} />}
              onClick={activeTab === "doctors" ? openAddDoctorModal : openAddLaboratoryModal}>
              {activeTab === "doctors" ? t("add-doctor") : t("add-laboratory")}
            </Button></PermissionGate>
          </Group>

          <TabsContent style={{paddingBottom: "200px"}} value="doctors">
            <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
              <Table
                ref={tableRef}
                withRowBorders={true}
                mt={"1rem"}
                miw={1280}
                withTableBorder
                highlightOnHover
                highlightOnHoverColor="var(--clr-gray-100)">
                <LoadingOverlay
                  visible={isRefetching && !!data?.newData?.length && !isMounted && !isUpdatingProfile}
                  zIndex={1000}
                  overlayProps={{ blur: 0.1, opacity: 0.6 }}
                  loaderProps={{ display: "none" }}
                />
                <Table.Thead>
                  <Table.Tr bg={"var(--clr-gray-100)"}>
                    <Table.Th>{t("doctor")}</Table.Th>
                    <Table.Th>{t("license-number")}</Table.Th>
                    <Table.Th>{t("expertise")}</Table.Th>
                    <Table.Th>{t("specialties")}</Table.Th>
                    <Table.Th>{t("visibility")}</Table.Th>
                    <Table.Th>{t("action")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {renderLoadingErrorData({
                    isLoading: isLoading && fetchStatus === "fetching",
                    isError,
                    isSuccess,
                    dataLength: tableData.length,
                    errorComponent: (
                      <TableErrorRow
                        colSpan={columnCount}
                        message={t("encountered-error")}
                      />
                    ),
                    loadingComponent: (
                      <TableSkeletonRows
                        columns={columnCount}
                        skeletonProps={{
                          widths: ["lg", "md", "lg", "lg", "md", "md"],
                        }}
                      />
                    ),
                    emptyDataComponent: (
                      <TableEmptyRow
                        colSpan={columnCount}
                        message={
                          debounced
                            ? t("empty-results-for", { searchFilter: `"${debounced}"` })
                            : t("no-doctors-available")
                        }
                      />
                    ),
                    dataComponent: doctorRows,
                  })}
                </Table.Tbody>
              </Table>
              <TablePagination
              paginationData={data?.pagination || {}}
              paginatedRows={data?.newData?.length || 0}
              onPageChange={(page) => setTablePagination(prev => ({ ...prev, page }))}
              isLoading={isLoading}
            />
            </ScrollArea>
            
            
          </TabsContent>

          <TabsContent style={{paddingBottom: "200px"}} value="laboratories">
            <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
              <Table
                ref={tableRef}
                withRowBorders={true}
                mt={"1rem"}
                miw={1280}
                withTableBorder
                highlightOnHover
                highlightOnHoverColor="var(--clr-gray-100)">
                <Table.Thead>
                  <Table.Tr bg={"var(--clr-gray-100)"}>
                    <Table.Th>{t("laboratory-actual")}</Table.Th>
                    <Table.Th>{t("location")}</Table.Th>
                    <Table.Th>{t("years-of-service")}</Table.Th>
                    <Table.Th>{t("bio")}</Table.Th>
                    <Table.Th>{t("visibility")}</Table.Th>
                    <Table.Th>{t("action")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {renderLoadingErrorData({
                    isLoading: isLaboratoriesLoading && laboratoriesFetchStatus === "fetching",
                    isError: isLaboratoriesError,
                    isSuccess: isLaboratoriesSuccess,
                    dataLength: laboratoriesData.length,
                    errorComponent: (
                      <TableErrorRow
                        colSpan={6}
                        message={t("encountered-error")}
                      />
                    ),
                    loadingComponent: (
                      <TableSkeletonRows
                        columns={6}
                        skeletonProps={{
                          widths: ["lg", "md", "md", "md", "md", "md"],
                        }}
                      />
                    ),
                    emptyDataComponent: (
                      <TableEmptyRow
                        colSpan={6}
                        message={
                          laboratoriesDebounced
                            ? t("empty-results-for", { searchFilter: `"${laboratoriesDebounced}"` })
                            : t("no-laboratories-available")
                        }
                      />
                    ),
                    dataComponent: laboratoryRows,
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            <TablePagination
              paginationData={laboratoriesApiData?.pagination || {}}
              paginatedRows={laboratoriesApiData?.newData?.length || 0}
              onPageChange={(page) => setLaboratoriesPagination(prev => ({ ...prev, page }))}
              isLoading={isLaboratoriesLoading}
            />
            
          </TabsContent>
        </TabsRoot>

        {/* Modals */}
        <PermissionGate permission="profiles.create"><AddDoctorModal
          opened={addDoctorModalOpened}
          close={closeAddDoctorModal}
        /></PermissionGate>

        <PermissionGate permission="profiles.edit"><EditDoctorModal
          opened={editDoctorModalOpened}
          close={() => {
            closeEditDoctorModal();
            setSelectedProfileUserId(null);
          }}
          userId={selectedProfileUserId}
        /></PermissionGate>

        <PermissionGate permission="profiles.create"><AddLaboratoryModal
          opened={addLaboratoryModalOpened}
          close={closeAddLaboratoryModal}
        /></PermissionGate>

        <PermissionGate permission="profiles.edit"><EditLaboratoryModal
          opened={editLaboratoryModalOpened}
          close={() => {
            closeEditLaboratoryModal();
            setSelectedProfileUserId(null);
          }}
          userId={selectedProfileUserId}
        /></PermissionGate>
      </StyledResourceManagement>
    </StyledLaboratory>
  );
};
